import React, { useCallback, useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { Outlet } from "react-router";
import { useLocation, useNavigate } from "react-router-dom";
import FeedHeader from "../Components/Functionality/FeedHeader";
import FilePopup from "../Components/Functionality/PDFViewer";
import Version from "../Components/UI/Version";
import { useProfile } from "../Context/openProfileContext";
import { useSocket } from "../Context/SocketContext";
import {
  useDebatesStatusStore,
  useUserProfileStore,
  useUserStore,
  useWorkspaceStore,
} from "../Context/store";
import { useSubworkspaceStore } from "../Context/store";
import AgentLayout from "../Pages/GlobalAgent/AgentLayout";
import NotificationDrawer from "../Pages/Notification/NotificationDrawer";
import ProfileDrawer from "../Pages/Profile/ProfileDrawer";
import useDebounce from "../Services/useDebounce";
import { extractPathname } from "../Utils/Helper";
import Header from "./Header/Header";
import MobileLayout from "./MobileLayout";
import FeedSidebar from "./Sidebar/FeedSidebar";
import Sidebar from "./Sidebar/Sidebar";
import {
  useGetWorkspaceWiseForumStatus,
  useWorkspacesByUserId,
} from "../Web-Hooks/Workspace/use-workspace";
import { useGetUserById } from "../Web-Hooks/Profile/use-profile";
import {
  useGetSubWorkspaceUserBySubId,
  useSubWorkspacesByWorkspaceId,
} from "../Web-Hooks/Workspace/use-subworkspace";
import toast from "react-hot-toast";
import { useSignalR } from "../hooks/useSignalR";
import FeedSidebar_V1 from "./Sidebar_V1/FeedSidebar_V1";

const HomeLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: loggedInUser, setUser } = useUserStore();
  const { selectedWorkspace, setWorkspaceList, selectWorkspace } =
    useWorkspaceStore();
  const {
    SubWorkspaceUser,
    selectedSubWorkspace,
    setSubWorkspaceList,
    setSubWorkspaceUser,
    selectSubWorkspace,
  } = useSubworkspaceStore();
  const { setProfileDetail } = useUserProfileStore();
  const { setAllForumStatus, setCurrentForumStatus } = useDebatesStatusStore();
  const { openRight, setOpenRight } = useProfile();
  const { initializeConnection } = useSocket();
  const [search, setSearch] = useState("");
  const { pathname, search: searchQuery } = location;
  const query = new URLSearchParams(searchQuery);
  const userName = query?.get("userName");
  const userRoleForWorkspace =
    selectedWorkspace?.createdBy?.userId === loggedInUser?.userId
      ? "Admin"
      : "Member";
  const [openNotification, setOpenNotification] = useState(false);
  const openDrawerNotification = () => setOpenNotification(true);
  const closeDrawerNotification = () => setOpenNotification(false);
  const { sendMessage, connection } = useSignalR();

  const openDrawerRight = () => {
    setOpenRight(true);
  };
  const closeDrawerRight = () => setOpenRight(false);
  const isTab = extractPathname(pathname) === "notification";
  const { data: workspaceList } = useWorkspacesByUserId(loggedInUser?.userId);
  const { data: subworkspaceList, isLoading: subworkspaceLoading } =
    useSubWorkspacesByWorkspaceId(
      selectedWorkspace?.workSpaceId,
      loggedInUser?.userId
    );
  const { data: subworkspaceUserList } = useGetSubWorkspaceUserBySubId(
    selectedSubWorkspace?.subWorkSpaceId
  );
  const { data: allForumStatus } = useGetWorkspaceWiseForumStatus({
    userId: loggedInUser?.userId,
    workspaceId: selectedWorkspace.workSpaceId,
  });

  const { data: ProfileDetails } = useGetUserById(
    loggedInUser?.userId,
    selectedSubWorkspace?.subWorkSpaceId,
    selectedWorkspace?.workSpaceId
  );
  const debounceSearch = useDebounce(search, 500);

  const handleSearch = useCallback(
    () => {
      if (debounceSearch.trim() === "") {
        pathname === "/search" &&
          navigate({ pathname: "/feed-public", search: "" });
      } else {
        pathname !== "/search"
          ? navigate(`/search?search=${encodeURIComponent(debounceSearch)}`)
          : navigate({
              pathname: pathname,
              search: `?search=${encodeURIComponent(debounceSearch)}`, // remove # from search query
            });
      }
    }, // eslint-disable-next-line
    [debounceSearch, navigate]
  );

  useEffect(() => {
    if (allForumStatus) {
      setAllForumStatus(allForumStatus);
      const forumStatus =
        allForumStatus?.forums?.[selectedSubWorkspace?.subWorkSpaceId];
      setCurrentForumStatus(forumStatus);
    }
  }, [
    allForumStatus,
    selectedSubWorkspace?.subWorkSpaceId,
    setAllForumStatus,
    setCurrentForumStatus,
  ]);

  // On refresh initialize socket connection
  useEffect(() => {
    if (loggedInUser?.userId) {
      initializeConnection(loggedInUser?.userId, loggedInUser?.userName);
    }
  }, [loggedInUser, initializeConnection]);

  // On refresh set workspace
  useEffect(() => {
    setWorkspaceList(workspaceList);
    if (workspaceList) {
      const findExistWorkspace = workspaceList?.find(
        (workspace) => workspace?.workSpaceId === selectedWorkspace?.workSpaceId
      );
      if (!findExistWorkspace) {
        toast.error("Workspace not found, please select another workspace");
        localStorage.removeItem("selectedWorkspace");
        localStorage.removeItem("selectedSubWorkspace");
        navigate("/createworkspace");
      } else if (findExistWorkspace) {
        selectWorkspace(findExistWorkspace);
      }
    }
  }, [workspaceList, setWorkspaceList, navigate]);

  // On refresh set subworkspace
  useEffect(() => {
    setSubWorkspaceList(subworkspaceList);
    if (subworkspaceList && !subworkspaceLoading) {
      const findExistSubWorkspace = subworkspaceList?.find(
        (sub) => sub?.subWorkSpaceId === selectedSubWorkspace?.subWorkSpaceId
      );
      if (findExistSubWorkspace) {
        selectSubWorkspace(findExistSubWorkspace);
      } else {
        selectSubWorkspace(subworkspaceList[0]);
        navigate("/feed", { replace: true });
      }
    }
  }, [subworkspaceList, setSubWorkspaceList, selectSubWorkspace, navigate]);

  // set subworkspace user globally and on refresh set user globally
  useEffect(() => {
    setSubWorkspaceUser(subworkspaceUserList);
  }, [subworkspaceUserList, setSubWorkspaceUser]);

  // Set required user details on load
  useEffect(() => {
    setProfileDetail(ProfileDetails);
    setUser({
      userName: ProfileDetails?.userName,
      role: ProfileDetails?.subworkspaceRole || userRoleForWorkspace,
      userImage: ProfileDetails?.userImage,
      userUsedStorage: ProfileDetails?.userStorage,
    });
  }, [ProfileDetails, setProfileDetail, setUser, userRoleForWorkspace]);

  // Global Search Functionality
  useEffect(() => {
    handleSearch();
  }, [handleSearch]);

  // Join the workspace group socket connection
  useEffect(() => {
    if (connection?.state === "Connected") {
      const connectServer = async () => {
        return await sendMessage(
          "JoinWorkspaceGroup",
          selectedWorkspace?.workSpaceId
        );
      };
      connectServer().then((res) =>
        console.log(`${selectedWorkspace?.workSpaceId} is connected: ${res}`)
      );
    }
  }, [sendMessage, selectedWorkspace?.workSpaceId, connection?.state]);

  // When the path changes, check if it's outside "create-debate" paths
  useEffect(() => {
    if (!location.pathname.startsWith("/createDebate")) {
      localStorage.removeItem("Debate");
      localStorage.removeItem("Citation");
      localStorage.removeItem("Viewers");
    }
  }, [location.pathname]);

  const excludedRoutes = [
    "/createDebate",
    "/library",
    "/role",
    "/notification",
    "/reference",
    "/debate/",
    "/messageOption/",
    "/group-message/",
  ];

  const shouldShowGlobalAIAgent = !excludedRoutes.some((route) =>
    pathname.includes(route)
  );

  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  return (
    <div className="h-screen">
      <FilePopup />
      <Version />

      {shouldShowGlobalAIAgent && <AgentLayout />}
      {!isMobile ? (
        <div className=" h-full bg-sideLayoutPrimary">
          <header className="h-16 ">
            <Header
              setSearch={setSearch}
              search={search}
              openProfile={openDrawerRight}
              openDrawerNotification={openDrawerNotification}
            />{" "}
          </header>
          <section className="flex h-[calc(100vh-4rem)]">
            <section className="w-[72px] h-full bg-primary">
              <Sidebar openProfile={openDrawerRight} />
            </section>
            <section className="w-64 h-full bg-secondary rounded-l-md">
              { process.env.REACT_APP_FEATURE_FORUM_DEBATES === "true" ? <FeedSidebar_V1 /> : <FeedSidebar/> }
            </section>
            <section className="flex-1 bg-white relative">
              {extractPathname(pathname)[0] !== "createprofile" &&
                extractPathname(pathname)[0] !== "messageOption" &&
                extractPathname(pathname)[0] !== "group-message" && (
                  <div className="h-16">
                    <FeedHeader
                      apiType={pathname}
                      isTab={isTab}
                      AvtarStackCount={SubWorkspaceUser?.length}
                      userName={userName}
                    />
                  </div>
                )}
              <div
                className={`md:w-[calc(100vw-20.5rem)] h-[calc(100vh-8rem)] ${
                  extractPathname(pathname)[0] !== "messageOption" &&
                  extractPathname(pathname)[0] !== "group-message" &&
                  "overflow-y-auto"
                }`}
              >
                <Outlet />
              </div>
            </section>
          </section>
        </div>
      ) : (
        <div className="h-full">
          <MobileLayout
            search={search}
            setSearch={setSearch}
            isTab={isTab}
            openProfile={openDrawerRight}
          >
            <Outlet />
          </MobileLayout>
        </div>
      )}

      <ProfileDrawer
        openRight={openRight}
        closeDrawerRight={closeDrawerRight}
      />
      <NotificationDrawer
        openNotification={openNotification}
        closeOpenNotification={closeDrawerNotification}
      />
    </div>
  );
};

export default HomeLayout;
